'use client'

/**
 * Horizontal app bar (PROD-1403).
 *
 * Replaces the click-to-expand app list at the top of the sidebar: every app
 * the user can open is ALWAYS one click away in a row of icons across the
 * top bar. Pinned apps lead, left of a divider. Like Notion's favorites, you
 * drag an app left of the divider to pin it, drag pinned apps to reorder
 * them, and drag one back out to unpin it; right-click → Pin/Unpin works too.
 * Pins follow the user across apps (`../app-pins`). The first nine
 * tiles answer ⌘1…⌘9 and the next nine ⌥⌘1…⌥⌘9 (Ctrl / Ctrl+Alt on
 * Windows/Linux, and Ctrl also works on macOS for browsers that keep
 * ⌘+digit for tab switching).
 *
 * The bar shows only when EVERY icon fits across the space it is given. When
 * they don't (a narrow window, a phone) it renders an empty spacer and tells
 * the sidebar, through `../app-bar-state`, to bring back the expandable app
 * list — on phones that list lives in the drawer, as before.
 *
 * `AgentTopbar` (`@ordo/agent-embed`) mounts this in every app's top bar, so
 * hosts need no wiring. `useAppBarApps` is exported for other launcher
 * surfaces (the ⌘K palette's "Apps" section) so they show the same order and
 * the same shortcut numbers.
 */
import { Pin, PinOff } from 'lucide-react'
import * as React from 'react'

import { setAppBarShowing, useAppBarState } from '../app-bar-state'
import { appIconStyle } from '../app-icon-style'
import { movePin, pinTargetAt, usePinnedApps } from '../app-pins'
import { cn } from '../cn'
import { useAppFavicon } from '../hooks/use-app-favicon'
import { type OrdoAppLink, useOrdoApps } from '../hooks/use-ordo-apps'
import { useShellLayout } from './app-shell'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'

// Two rows of digit shortcuts: ⌘1…⌘9 for tiles 1–9, then ⌥⌘1…⌥⌘9 for
// tiles 10–18 (Ctrl / Ctrl+Alt off macOS). Same gesture, one more modifier;
// ⌘0 resets zoom and ⌘⇧3/4/5 are macOS screenshots, so neither can be a row.
const ROW_SIZE = 9
const MAX_SHORTCUTS = ROW_SIZE * 2

// Geometry, in px. A tile button is `h-10 w-10`, tiles within a group sit
// `gap-1` apart, and the pinned/other divider is 1px wide with `mx-2` (it
// replaces the gap between the two groups). `appBarWidth` uses the same
// numbers, so "fits" means exactly what renders.
const TILE_PX = 40
const TILE_GAP_PX = 4
const DIVIDER_PX = 1 + 2 * 8

export interface AppBarEntry {
  app: OrdoAppLink
  pinned: boolean
  /** 1–18 for the first eighteen tiles (1–9 = ⌘1…⌘9, 10–18 = ⌥⌘1…⌥⌘9), else null. */
  shortcut: number | null
}

/** Pinned apps first (in pin order), then the rest in registry order. Exported for tests. */
export function orderAppBarApps(apps: OrdoAppLink[], pinnedIds: string[]): AppBarEntry[] {
  const byId = new Map(apps.map((a) => [a.id, a]))
  const pinned = pinnedIds.map((id) => byId.get(id)).filter((a): a is OrdoAppLink => !!a)
  const pinnedSet = new Set(pinned.map((a) => a.id))
  const rest = apps.filter((a) => !pinnedSet.has(a.id))
  return [...pinned, ...rest].map((app, i) => ({
    app,
    pinned: pinnedSet.has(app.id),
    shortcut: i < MAX_SHORTCUTS ? i + 1 : null,
  }))
}

/** Width the bar needs to show every tile. Exported for tests. */
export function appBarWidth(tileCount: number, hasDivider: boolean): number {
  if (tileCount === 0) {
    return 0
  }
  const gaps = Math.max(0, tileCount - 1 - (hasDivider ? 1 : 0)) * TILE_GAP_PX
  return tileCount * TILE_PX + gaps + (hasDivider ? DIVIDER_PX : 0)
}

/** The bar only earns its space when there is somewhere else to go. Exported for tests. */
export function shouldShowAppBar(entryCount: number, isLoading: boolean, hideUntilLoaded: boolean): boolean {
  if (isLoading && entryCount === 0) {
    return !hideUntilLoaded
  }
  return entryCount >= 2
}

export function useAppBarApps(): { entries: AppBarEntry[]; isLoading: boolean } {
  const { apps, isLoading } = useOrdoApps()
  const { pinnedIds } = usePinnedApps()
  const entries = React.useMemo(() => orderAppBarApps(apps, pinnedIds), [apps, pinnedIds])
  return { entries, isLoading }
}

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
}

/** Keycaps for tile `n`: ["⌘", "1"] / ["⌥", "⌘", "1"] on macOS, ["Ctrl", "1"] / ["Ctrl", "Alt", "1"] elsewhere. */
export function appShortcutKeys(n: number, mac: boolean = isMac()): string[] {
  const secondRow = n > ROW_SIZE
  const digit = String(secondRow ? n - ROW_SIZE : n)
  if (mac) {
    return secondRow ? ['⌥', '⌘', digit] : ['⌘', digit]
  }
  return secondRow ? ['Ctrl', 'Alt', digit] : ['Ctrl', digit]
}

/** Human label for tile `n`: "⌘1" / "⌥⌘1" on macOS, "Ctrl+1" / "Ctrl+Alt+1" elsewhere. */
export function formatAppShortcut(n: number, mac: boolean = isMac()): string {
  const keys = appShortcutKeys(n, mac)
  return mac ? keys.join('') : keys.join('+')
}

/**
 * The tile (1–18) an app shortcut points at, else null: ⌘/Ctrl + digit is
 * 1–9, adding ⌥/Alt is 10–18. The digit comes from `code` ("Digit1") because
 * Option rewrites `key` on macOS (⌥1 reports "¡"); `key` is the fallback for
 * the plain row. Typed loosely and reads both optionally for the same reason
 * as Ordo's ⌘K guard: autofill shims dispatch keydowns with neither.
 * Exported for tests.
 */
export function appShortcutSlot(e: {
  key?: string
  code?: string
  metaKey?: boolean
  ctrlKey?: boolean
  altKey?: boolean
  shiftKey?: boolean
}): number | null {
  if ((!e.metaKey && !e.ctrlKey) || e.shiftKey) {
    return null
  }
  const fromCode = /^Digit([1-9])$/.exec(e.code ?? '')?.[1]
  const fromKey = !e.altKey && e.key && e.key.length === 1 && e.key >= '1' && e.key <= '9' ? e.key : undefined
  const digit = fromCode ?? fromKey
  if (!digit) {
    return null
  }
  return Number(digit) + (e.altKey ? ROW_SIZE : 0)
}

const TILE_SIZE_CLASS = {
  sm: { box: 'h-7 w-7', icon: '[&>svg]:h-4 [&>svg]:w-4', plain: 'rounded-md' },
  md: { box: 'h-8 w-8', icon: '[&>svg]:h-[18px] [&>svg]:w-[18px]', plain: 'rounded-lg' },
  lg: { box: 'h-14 w-14', icon: '[&>svg]:h-7 [&>svg]:w-7', plain: 'rounded-2xl' },
} as const

export interface AppLauncherTileProps {
  app: OrdoAppLink
  /** `sm` (28px) in the desktop app row, `md` (32px) on the mobile app button, `lg` (56px) in the mobile app grid. */
  size?: keyof typeof TILE_SIZE_CLASS
  /** Ring the tile, hugging it with a small gap, in the app's own color — marks the current app. */
  current?: boolean
  className?: string
}

/** One app's colored tile, from its HQ registry entry. Shared by the desktop app row and the mobile switcher. */
export function AppLauncherTile({ app, size = 'sm', current = false, className }: AppLauncherTileProps) {
  const sizing = TILE_SIZE_CLASS[size]
  const ring = current ? 'ring-2 ring-primary ring-offset-2 ring-offset-card' : undefined
  const ringStyle = current && app.bgColor ? ({ '--tw-ring-color': app.bgColor } as React.CSSProperties) : undefined
  if (app.logo) {
    return (
      <span
        aria-hidden
        style={ringStyle}
        className={cn(
          'inline-flex overflow-hidden rounded-[25%] ring-1 ring-border [&>svg]:h-full [&>svg]:w-full',
          sizing.box,
          ring,
          className,
        )}>
        {app.logo}
      </span>
    )
  }
  if (app.image) {
    return <img src={app.image} alt="" style={ringStyle} className={cn(sizing.box, sizing.plain, ring, className)} />
  }
  const brand = app.bgColor && app.fgColor
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex items-center justify-center',
        sizing.box,
        sizing.icon,
        brand ? 'rounded-[25%]' : cn(sizing.plain, 'bg-muted text-foreground/70'),
        ring,
        className,
      )}
      style={brand ? { ...appIconStyle(app.bgColor!, app.fgColor!), ...ringStyle } : ringStyle}>
      {app.icon}
    </span>
  )
}

/** Where a drop lands: in front of a pinned app, the end of the pinned group, or unpinned. */
type DropTarget = string | null | 'unpin'

const DRAG_MIME = 'application/x-ordo-app'

/**
 * Accept an app drag over an element. Both `dragenter` AND `dragover` must
 * be cancelled: a quick drag can be released right after entering a tile,
 * before any `dragover` fires there, and the browser then refuses the drop
 * (seen with a real mouse — the drop silently did nothing).
 */
function acceptDrag(onOver: () => void, stop = false) {
  const handler = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) {
      return
    }
    e.preventDefault()
    if (stop) {
      e.stopPropagation()
    }
    e.dataTransfer.dropEffect = 'move'
    onOver()
  }
  return { onDragEnter: handler, onDragOver: handler }
}

interface AppBarItemProps {
  entry: AppBarEntry
  active: boolean
  /** Ring the current app's tile (unified shell, where no sidebar header names it). */
  showCurrent: boolean
  onTogglePin: (id: string) => void
  onDragStart: (id: string) => void
  onDragEnd: () => void
  /**
   * Drop target under the pointer: for a pinned tile, in front of it or (right
   * half) after it; 'unpin' for every unpinned tile.
   */
  targetAt: (rightHalf: boolean) => DropTarget
  onDragOverTarget: (target: DropTarget) => void
  onDropOn: (target: DropTarget) => void
  /** Show the insertion marker on this tile's leading edge. */
  insertBefore: boolean
  /** Show it on the trailing edge: the end of the pinned group, on its last tile. */
  insertAfter?: boolean
}

/** Is the pointer over the right half of the element handling this drag event? */
const onRightHalf = (e: React.DragEvent) => {
  const rect = e.currentTarget.getBoundingClientRect()
  return e.clientX > rect.left + rect.width / 2
}

function AppBarItem({
  entry,
  active,
  showCurrent,
  onTogglePin,
  onDragStart,
  onDragEnd,
  targetAt,
  onDragOverTarget,
  onDropOn,
  insertBefore,
  insertAfter = false,
}: AppBarItemProps) {
  const { app, pinned, shortcut } = entry
  const [menuOpen, setMenuOpen] = React.useState(false)
  const accept = (e: React.DragEvent) => acceptDrag(() => onDragOverTarget(targetAt(onRightHalf(e))), true)
  return (
    <li
      className="relative flex shrink-0"
      onDragEnter={(e) => accept(e).onDragEnter(e)}
      onDragOver={(e) => accept(e).onDragOver(e)}
      onDrop={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onDropOn(targetAt(onRightHalf(e)))
      }}>
      {insertBefore && (
        <span aria-hidden className="absolute -left-[3px] top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />
      )}
      {insertAfter && (
        <span aria-hidden className="absolute -right-[3px] top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={app.href}
            data-app-bar-tile
            aria-label={app.name}
            aria-current={active ? 'page' : undefined}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(DRAG_MIME, app.id)
              e.dataTransfer.effectAllowed = 'move'
              // Chrome aborts a native drag when the layout changes during
              // `dragstart` itself; the drag state adds the "Drop to pin"
              // slot, so apply it on the next tick.
              window.setTimeout(() => onDragStart(app.id), 0)
            }}
            onDragEnd={onDragEnd}
            onContextMenu={(e) => {
              e.preventDefault()
              setMenuOpen(true)
            }}
            className={cn(
              // `transform` carries the dock magnify (see useDockMagnify).
              'relative flex h-10 w-10 items-center justify-center rounded-xl transition-[background-color,transform] duration-150 ease-out',
              'hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              // Classic shell: no visual "selected" state — the sidebar
              // header right below already names the current app. The
              // unified shell drops that header, so the tile is ringed.
              // `aria-current` marks it for assistive tech either way.
            )}>
            <AppLauncherTile app={app} current={active && showCurrent} />
          </a>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={6} className="flex items-center gap-2">
          <span>{app.name}</span>
          {shortcut !== null && <span className="text-xs opacity-60">{formatAppShortcut(shortcut)}</span>}
        </TooltipContent>
      </Tooltip>
      {/* The pin menu opens on right-click only. Its trigger is an inert
          overlay used purely as the positioning anchor, so left-click and
          Enter on the tile keep their plain-link behavior. */}
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <span aria-hidden tabIndex={-1} className="pointer-events-none absolute inset-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="start" sideOffset={6}>
          <DropdownMenuItem onSelect={() => onTogglePin(app.id)}>
            {pinned ? <PinOff className="mr-2 h-4 w-4" /> : <Pin className="mr-2 h-4 w-4" />}
            {pinned ? 'Unpin' : 'Pin to front'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  )
}

export interface AppBarProps {
  /** Id of the app the page belongs to. Defaults to the id the sidebar publishes. */
  currentAppId?: string
  /**
   * Render nothing until the registry has loaded, instead of placeholder
   * tiles. For customer-facing hosts (Ordo), where most viewers have one app
   * and must never see a flash of internal tooling.
   */
  hideUntilLoaded?: boolean
  className?: string
}

/**
 * Takes the remaining width of the top bar (`flex-1`). Always renders its
 * container — that is what gets measured — and fills it with tiles only when
 * they all fit. When there is nothing to show (one app, or still loading with
 * `hideUntilLoaded`) the container takes no space at all, so a host's own
 * layout (Ordo's centered search for customers) is untouched.
 */
/** How far from the pointer a tile still grows, in px, and how much the tile under it grows. */
export const DOCK_RADIUS_PX = 90
export const DOCK_MAX_BOOST = 0.22

/**
 * A tile's scale for the dock magnify: full boost under the pointer, easing
 * to none at DOCK_RADIUS_PX along a cosine, like the macOS dock but subtler.
 * Pure; exported for tests.
 */
export function dockScale(distancePx: number, radius = DOCK_RADIUS_PX, boost = DOCK_MAX_BOOST): number {
  const d = Math.abs(distancePx)
  if (d >= radius) {
    return 1
  }
  return 1 + boost * (0.5 + 0.5 * Math.cos((Math.PI * d) / radius))
}

/**
 * macOS-dock magnify for the tiles under `navRef`: as the pointer moves along
 * the bar, the tile under it and its neighbours grow a little. Purely a
 * transform, so the bar's width and the fit check never change. Off for
 * reduced motion and for touch (no hover), and set imperatively per frame so
 * moving the mouse never re-renders the bar.
 */
function useDockMagnify(navRef: React.RefObject<HTMLElement | null>, enabled: boolean) {
  React.useEffect(() => {
    const nav = navRef.current
    if (!enabled || !nav || typeof window === 'undefined') {
      return
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return
    }
    let frame = 0
    const tiles = () => Array.from(nav.querySelectorAll<HTMLElement>('[data-app-bar-tile]'))
    const reset = () => {
      cancelAnimationFrame(frame)
      for (const tile of tiles()) {
        tile.style.transform = ''
      }
    }
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') {
        return
      }
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        for (const tile of tiles()) {
          // Scaling about the centre leaves the centre where it was.
          const rect = tile.getBoundingClientRect()
          const scale = dockScale(e.clientX - (rect.left + rect.width / 2))
          tile.style.transform = scale === 1 ? '' : `scale(${scale.toFixed(3)})`
        }
      })
    }
    nav.addEventListener('pointermove', onMove)
    nav.addEventListener('pointerleave', reset)
    // A drag starts from a magnified tile; settle the bar while it runs.
    nav.addEventListener('dragstart', reset)
    return () => {
      nav.removeEventListener('pointermove', onMove)
      nav.removeEventListener('pointerleave', reset)
      nav.removeEventListener('dragstart', reset)
      reset()
    }
  }, [navRef, enabled])
}

export function AppBar({ currentAppId: currentAppIdProp, hideUntilLoaded = false, className }: AppBarProps) {
  const { entries, isLoading } = useAppBarApps()
  const { pinnedIds, togglePin, setPins } = usePinnedApps()
  const { currentAppId: publishedAppId } = useAppBarState()

  // Notion-style drag to pin / reorder / unpin.
  const [draggingId, setDraggingId] = React.useState<string | null>(null)
  const [overTarget, setOverTarget] = React.useState<DropTarget | undefined>(undefined)
  const endDrag = React.useCallback(() => {
    setDraggingId(null)
    setOverTarget(undefined)
  }, [])
  const dropOn = React.useCallback(
    (target: DropTarget) => {
      if (draggingId) {
        setPins(movePin(pinnedIds, draggingId, target))
      }
      endDrag()
    },
    [draggingId, pinnedIds, setPins, endDrag],
  )
  const dragProps = {
    onTogglePin: togglePin,
    onDragStart: setDraggingId,
    onDragEnd: endDrag,
    onDragOverTarget: setOverTarget,
    onDropOn: dropOn,
  }
  // Dropping on the pinned group's empty space / divider appends to it.
  const pinZoneProps = {
    ...acceptDrag(() => setOverTarget(null)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      dropOn(null)
    },
  }
  const currentAppId = currentAppIdProp ?? publishedAppId
  const showCurrent = useShellLayout() === 'unified'
  // Hosts without a sidebar (Leads) get their registry favicon from here.
  useAppFavicon(currentAppIdProp)

  const pinned = entries.filter((e) => e.pinned)
  const rest = entries.filter((e) => !e.pinned)
  const pinnedOrder = pinned.map((e) => e.app.id)
  const loadingPlaceholders = isLoading && entries.length === 0
  const wanted = shouldShowAppBar(entries.length, isLoading, hideUntilLoaded)
  const needed = loadingPlaceholders
    ? appBarWidth(6, false)
    : appBarWidth(entries.length, pinned.length > 0 && rest.length > 0)

  // Measure the space we were given, before paint, so the sidebar never
  // flashes the wrong mode.
  const containerRef = React.useRef<HTMLDivElement>(null)
  const navRef = React.useRef<HTMLElement>(null)
  const [available, setAvailable] = React.useState<number | null>(null)
  React.useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) {
      return
    }
    setAvailable(el.clientWidth)
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(() => setAvailable(el.clientWidth))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const fits = available !== null && needed <= available
  const showing = wanted && fits
  // The nav only mounts while the bar shows, so attach once it does.
  useDockMagnify(navRef, showing)
  React.useLayoutEffect(() => {
    setAppBarShowing(showing)
  }, [showing])
  React.useEffect(() => () => setAppBarShowing(false), [])

  // ⌘1…⌘9 (then ⌥⌘1…⌥⌘9) / Ctrl(+Alt)+1…9 jumps to the Nth app, whether
  // or not the bar is showing. preventDefault so browsers that let pages claim the combo don't
  // also switch tabs.
  const entriesRef = React.useRef(entries)
  entriesRef.current = entries
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const slot = appShortcutSlot(e)
      if (slot === null) {
        return
      }
      const target = entriesRef.current[slot - 1]
      if (!target) {
        return
      }
      e.preventDefault()
      if (target.app.id !== currentAppId) {
        window.location.href = target.app.href
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [currentAppId])

  return (
    <div ref={containerRef} className={cn('flex min-w-0 items-center', wanted ? 'flex-1' : 'flex-none', className)}>
      {showing ? (
        <TooltipProvider delayDuration={150}>
          <nav ref={navRef} aria-label="Apps" className="flex items-center">
            {loadingPlaceholders ? (
              <ul className="flex items-center gap-1" aria-hidden>
                {Array.from({ length: 6 }, (_, i) => (
                  <li key={i} className="flex h-10 w-10 items-center justify-center">
                    <span className="h-7 w-7 animate-pulse rounded-md bg-muted" />
                  </li>
                ))}
              </ul>
            ) : (
              <>
                {pinned.length > 0 ? (
                  <>
                    <ul className="flex items-center gap-1" {...pinZoneProps}>
                      {pinned.map((entry, index) => (
                        <AppBarItem
                          key={entry.app.id}
                          entry={entry}
                          active={entry.app.id === currentAppId}
                          showCurrent={showCurrent}
                          targetAt={(rightHalf) => pinTargetAt(pinnedOrder, entry.app.id, rightHalf)}
                          insertBefore={draggingId !== null && overTarget === entry.app.id}
                          insertAfter={draggingId !== null && overTarget === null && index === pinned.length - 1}
                          {...dragProps}
                        />
                      ))}
                    </ul>
                    {rest.length > 0 && (
                      // The padding, not a margin, spaces the divider, so the
                      // whole gap takes a drop to the end of the pinned group.
                      <span aria-hidden {...pinZoneProps} className="flex h-10 shrink-0 items-center px-2">
                        <span className="h-6 w-px bg-border" />
                      </span>
                    )}
                  </>
                ) : (
                  draggingId !== null && (
                    // Nothing pinned yet: while dragging, offer a slot to start the group.
                    <div
                      {...pinZoneProps}
                      className={cn(
                        'mr-2 flex h-9 shrink-0 items-center rounded-lg border border-dashed px-3 text-xs text-muted-foreground',
                        overTarget === null ? 'border-primary text-primary' : 'border-border',
                      )}>
                      Drop to pin
                    </div>
                  )
                )}
                <ul
                  className="flex items-center gap-1"
                  {...acceptDrag(() => setOverTarget('unpin'))}
                  onDrop={(e) => {
                    e.preventDefault()
                    dropOn('unpin')
                  }}>
                  {rest.map((entry) => (
                    <AppBarItem
                      key={entry.app.id}
                      entry={entry}
                      active={entry.app.id === currentAppId}
                      showCurrent={showCurrent}
                      targetAt={() => 'unpin'}
                      insertBefore={false}
                      {...dragProps}
                    />
                  ))}
                </ul>
              </>
            )}
          </nav>
        </TooltipProvider>
      ) : null}
    </div>
  )
}
